import { useMutation, useQuery, useQueryClient } from 'react-query';
import axiosInstance from '../config/Axios';
import { useCustomToast } from '../pages/Contexts/ToastContext';

/**
 * Smart CRM — customer directory (Customers tab). Backend:
 * VenueCustomerController, every query scoped to the authed company.
 *
 *   GET    /customers?search&source&tag&is_subscribed&page&per_page
 *                                      → { data:[...], pagination:{current_page,total_pages,total,...} }
 *   GET    /customers/stats            → { data:{ total, new_this_month, subscribed, by_source } }
 *   POST   /customers                  manual add (dedupes on phone)
 *   GET    /customers/{id}             single
 *   PUT    /customers/{id}             edit (patch)
 *   DELETE /customers/{id}             soft delete
 *   POST   /customers/import           CSV (multipart `file`) or JSON `rows`
 *
 * List returns the full paginator (NOT unwrapped) so the DataTable lazy
 * paginator can read `pagination`. Single/stats/mutations unwrap `res?.data ?? res`.
 */

const LIST_KEY  = 'customers';
const STATS_KEY = 'customerStats';
const PROFILE_KEY = 'customerProfile';

/**
 * Customer 360 aggregate — GET /customers/{id}/profile → { data: { customer,
 * tier, stats, next_reward, insights[], timeline[], loyalty_enabled } }.
 * Unwrapped like the other single-object reads.
 */
export const useCustomerProfile = (id) =>
  useQuery(
    [PROFILE_KEY, id],
    async () => {
      const res = (await axiosInstance.get(`/customers/${id}/profile`)).data;
      return res?.data ?? res;
    },
    { enabled: !!id, staleTime: 30_000 }
  );

export const useGetCustomers = (params = {}) =>
  useQuery(
    [LIST_KEY, params],
    async () => (await axiosInstance.get('/customers', { params })).data,
    { keepPreviousData: true, staleTime: 15_000 }
  );

export const useGetCustomerStats = () =>
  useQuery(
    STATS_KEY,
    async () => {
      const res = (await axiosInstance.get('/customers/stats')).data;
      return res?.data ?? res;
    },
    { staleTime: 15_000 }
  );

export const useAddCustomer = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((payload) => axiosInstance.post('/customers', payload), {
    onSuccess: () => {
      qc.invalidateQueries(LIST_KEY);
      qc.invalidateQueries(STATS_KEY);
    },
    onError: (err) =>
      showToast('error', 'Customers', err.response?.data?.message ?? 'Could not save customer.'),
  });
};

export const useUpdateCustomer = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(({ id, ...payload }) => axiosInstance.put(`/customers/${id}`, payload), {
    onSuccess: () => {
      qc.invalidateQueries(LIST_KEY);
      qc.invalidateQueries(STATS_KEY);
    },
    onError: (err) =>
      showToast('error', 'Customers', err.response?.data?.message ?? 'Could not update customer.'),
  });
};

export const useDeleteCustomer = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation((id) => axiosInstance.delete(`/customers/${id}`), {
    onSuccess: () => {
      qc.invalidateQueries(LIST_KEY);
      qc.invalidateQueries(STATS_KEY);
    },
    onError: (err) =>
      showToast('error', 'Customers', err.response?.data?.message ?? 'Could not delete customer.'),
  });
};

export const useImportCustomers = () => {
  const qc = useQueryClient();
  const { showToast } = useCustomToast();
  return useMutation(
    (file) => {
      const form = new FormData();
      form.append('file', file);
      return axiosInstance.post('/customers/import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    {
      onSuccess: () => {
        qc.invalidateQueries(LIST_KEY);
        qc.invalidateQueries(STATS_KEY);
      },
      onError: (err) =>
        showToast('error', 'Customers', err.response?.data?.message ?? 'Import failed.'),
    }
  );
};
